import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { FormattedDate } from 'react-intl';

import { IconButton } from '@bibliocommons/base-buttons';
import { Delete as IconDelete, Edit as IconEdit, Plus as IconPlus } from '@bibliocommons/base-icons';
import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';
import { Modal, ModalContent, ModalFooter, ModalHeader } from '@bibliocommons/modal';
import { Table, TableHeader, TableHeading, TableBody, TableRow, TableCell } from '@bibliocommons/table';

import { LOCALE_CODE } from 'app/constants/language';
import { categoryConfigSummaryShape } from 'app/shapes/admin/browse/categories';

import './CategoryConfigsList.scss';

const CategoryConfigsList = ({
  categoryConfigByLocaleCode,
  isSaving,
  localeCodes,
  mapLocaleCodeToLabel,
  onCreateCategoryConfig,
  onLocaleCodeDelete,
  onSelectCategoryConfig,
  topLevelCategoryLabel
}) => {
  const [localeCodeToDelete, setLocaleCodeToDelete] = useState(null);

  const openConfirmDeleteModal = localeCode => {
    setLocaleCodeToDelete(localeCode);
  };

  const closeConfirmDeleteModal = () => {
    setLocaleCodeToDelete(null);
  };

  const handleDeleteClick = () => {
    onLocaleCodeDelete(localeCodeToDelete);
    closeConfirmDeleteModal();
  };

  const isConfirmDeleteModalOpen = Boolean(localeCodeToDelete);

  return (
    <>
      <Modal
        handleClose={closeConfirmDeleteModal}
        key="CategoryConfigsList_ConfirmDelete"
        open={isConfirmDeleteModalOpen}
      >
        <ModalHeader title="Delete category" />
        <ModalContent>
          <p>
            Are you sure you want to delete the &apos;{mapLocaleCodeToLabel(localeCodeToDelete)}&apos;{' '}
            {topLevelCategoryLabel} category?
          </p>
        </ModalContent>
        <ModalFooter handlePrimaryAction={handleDeleteClick} primaryActionButtonText="Delete category" />
      </Modal>

      <Table className="cp-admin-browse-categories-config-list">
        <TableHeader>
          <TableHeading>Language</TableHeading>
          <TableHeading>Creator</TableHeading>
          <TableHeading>Last updated</TableHeading>
          <TableHeading />
        </TableHeader>
        <TableBody>
          {localeCodes.map(localeCode => {
            const categoryConfig = categoryConfigByLocaleCode[localeCode];

            const { createdBy = '', updatedAt = '' } = categoryConfig || {};
            const isNew = !categoryConfig;

            const handleCreate = () => onCreateCategoryConfig(localeCode);
            const handleDelete = () => openConfirmDeleteModal(localeCode);
            const handleEdit = () => onSelectCategoryConfig(categoryConfig);

            return (
              <TableRow key={localeCode}>
                <TableCell className="table-cell">{mapLocaleCodeToLabel(localeCode)}</TableCell>
                <TableCell className="table-cell">{createdBy || ''}</TableCell>
                <TableCell className="table-cell">
                  {updatedAt > 0 && <FormattedDate day="2-digit" month="long" value={updatedAt} year="numeric" />}
                </TableCell>
                <TableCell className="table-cell">
                  <div className="actions">
                    {isNew ? (
                      <IconButton
                        className="action"
                        dataKey="action-create"
                        handleClick={handleCreate}
                        isSaving={isSaving}
                      >
                        <ScreenReaderMessage>Create</ScreenReaderMessage>
                        <IconPlus />
                      </IconButton>
                    ) : (
                      <>
                        <IconButton
                          className="action"
                          dataKey="action-edit"
                          handleClick={handleEdit}
                          isSaving={isSaving}
                        >
                          <ScreenReaderMessage>Edit</ScreenReaderMessage>
                          <IconEdit />
                        </IconButton>
                        <IconButton
                          className="action"
                          dataKey="action-delete"
                          handleClick={handleDelete}
                          isSaving={isSaving}
                        >
                          <ScreenReaderMessage>Delete</ScreenReaderMessage>
                          <IconDelete />
                        </IconButton>
                      </>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </>
  );
};

CategoryConfigsList.propTypes = {
  categoryConfigByLocaleCode: PropTypes.objectOf(categoryConfigSummaryShape).isRequired,
  isSaving: PropTypes.bool.isRequired,
  localeCodes: PropTypes.arrayOf(PropTypes.oneOf(Object.values(LOCALE_CODE))).isRequired,
  mapLocaleCodeToLabel: PropTypes.func.isRequired,
  onCreateCategoryConfig: PropTypes.func.isRequired,
  onLocaleCodeDelete: PropTypes.func.isRequired,
  onSelectCategoryConfig: PropTypes.func.isRequired,
  topLevelCategoryLabel: PropTypes.string.isRequired
};

export default CategoryConfigsList;
